import { extendableComponent } from '@graphcommerce/next-ui/Styles'
import { Trans } from '@lingui/react'
import { Box, Button, ButtonBase, Link, SxProps, Theme, Typography } from '@mui/material'
import { useRouter } from 'next/router'
import React from 'react'
import { setTitleClean } from '../GraphCMS/utils'
import { BlogTags } from './BlogTags'

export type BlogListItemProps = {
  asset: React.ReactNode
  url: string
  date: string
  title: string
  relatedPages: Array<{ title?: string | null | undefined; url: string }>
  sx?: SxProps<Theme>
}

const name = 'BlogListItem' as const
const parts = ['item', 'date', 'asset', 'title', 'relatedPages'] as const
const { classes } = extendableComponent(name, parts)

export function BlogListItem(props: BlogListItemProps & { readMoreCaption?: React.ReactNode }) {
  const { asset, url, date, title, relatedPages, sx = [], readMoreCaption } = props
  const { locale } = useRouter()
  const localeForFormat = locale === 'default' ? 'en' : locale
  const formatter = new Intl.DateTimeFormat(localeForFormat, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'Europe/Amsterdam',
  })

  return (
    <Box
      className={classes.item}
      sx={[
        (theme) => ({
          display: 'grid',
          gridTemplateRows: 'auto 1fr max-content',
          justifyItems: 'start',
          color: theme.palette.text.primary,
          gap: theme.spacings.xs,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <ButtonBase href={`/${url}`} sx={{ width: '100%' }}>
        <Box
          className={classes.asset}
          sx={(theme) => ({
            display: 'grid',
            overflow: 'hidden',
            width: '100%',
            aspectRatio: '2/3',
            position: 'relative',
            '& img': {
              height: '100% !important',
              objectFit: 'cover',
            },
            '& p': {
              alignSelf: 'center',
              justifySelf: 'center',
            },
            background: theme.palette.background.paper,
          })}
        >
          {asset}
          {relatedPages.length > 0 && (
            <Box
              sx={[
                (theme) => ({
                  position: 'absolute',
                  top: 'auto',
                  right: theme.spacings.xs,
                  bottom: theme.spacings.xs,
                  left: theme.spacings.xs,
                  maxWidth: `calc(100% - ${theme.spacings.xs})`,
                  '& > div': {
                    marginBottom: 0,
                  },
                }),
              ]}
            >
              <BlogTags relatedPages={relatedPages} />
            </Box>
          )}
        </Box>
      </ButtonBase>

      <Box>
        {date && (
          <Box
            component='time'
            className={classes.date}
            dateTime={date}
            sx={(theme) => ({
              display: 'block',
              mb: theme.spacings.xs,
              color: theme.palette.text.secondary,
            })}
          >
            {formatter.format(new Date(date))}
          </Box>
        )}

        <Link href={`/${url}`} className={classes.title} color='inherit' underline='hover'>
          <Typography
            component='h2'
            variant='h4'
            dangerouslySetInnerHTML={{ __html: setTitleClean(title) }}
          />
        </Link>
      </Box>

      <Button
        href={`/${url}`}
        className={classes.title}
        variant='outlined'
        color='primary'
        size='medium'
      >
        {readMoreCaption || <Trans id='Read more' />}
      </Button>
    </Box>
  )
}
